/*
 * Copyright '2025' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { ApolloClient, HttpLink, InMemoryCache } from "@apollo/client/core";
import { ApolloClientOptions } from "@apollo/client/core/ApolloClient";
import { onError } from "@apollo/client/link/error";
import { GraphQLError } from "graphql/error";
import { environment } from "../../../environments/environments";

export class TdmApolloClient {
  private readonly apolloClient: ApolloClient<unknown>;

  // Method to get the ApolloClient instance
  getClient(): ApolloClient<unknown> {
    // ApolloClient options
    const apolloClientOptions: ApolloClientOptions<unknown> = {
      // InMemoryCache for caching GraphQL data
      cache: new InMemoryCache({
        addTypename: false,
      }),
      // HttpLink for connecting to the GraphQL server
      // onError for handling network and GraphQL errors
      link: this.errorHandler()?.concat(
        new HttpLink({
          uri: environment.graphUrl,
          headers: { "apollographql-client-name": "tdp-tenant-custom-code" },
        })
      ),
    };
    // Return a new ApolloClient instance with the specified options
    return new ApolloClient(apolloClientOptions);
  }

  // Method to handle network and GraphQL errors
  private errorHandler() {
    return onError(({ operation, networkError, graphQLErrors }) => {
      const UNKNOWN_ERROR = "An unknown error occurred.";
      let issue: string;
      let errors = [];

      // If there's a network error, log it and add it to the errors array
      if (networkError) {
        issue = "networkError";
        errors = [new GraphQLError(networkError.message)];
      }
      // If there are GraphQL errors, log them and add them to the errors array
      else if (graphQLErrors) {
        issue = "graphQlErrors";
        errors = [...graphQLErrors];
      }
      // If there's an unknown error, log it and add it to the errors array
      else {
        issue = "Unknown error";
        errors = [new GraphQLError(UNKNOWN_ERROR)];
      }
      // For each error in the errors array, log it and throw it
      errors.forEach((error) => {
        // Suppress error, we do not need it in console.
        // console.error(`${issue} error: `, error.toString());
        throw error;
      });
    });
  }
}
